type FeaturebaseCommandCallback = (...args: unknown[]) => void

type FeaturebaseBootConfig = {
  appId: string
  featurebaseJwt: string
  language?: string
  theme?: 'light' | 'dark'
  hideDefaultLauncher?: boolean
}

type FeaturebaseIdentifyConfig = {
  organization: string
  featurebaseJwt: string
}

type FeaturebaseChangelogWidgetConfig = {
  organization: string
  theme: 'light' | 'dark'
  changelogCard: {
    enabled: boolean
  }
}

type FeaturebaseCommand =
  | 'boot'
  | 'identify'
  | 'show'
  | 'hide'
  | 'showNewMessage'
  | 'onHide'
  | 'onShow'
  | 'setLanguage'
  | 'init_changelog_widget'

declare global {
  interface Window {
    Featurebase?: (...args: unknown[]) => void
  }
}

const FEATUREBASE_SCRIPT_ID = 'featurebase-sdk'
const FEATUREBASE_SCRIPT_SRC = 'https://do.featurebase.app/js/sdk.js'
const FEATUREBASE_ENABLED = import.meta.env.VITE_FEATUREBASE_ENABLED === 'true'

let isBooted = false
let bootWaiters: Array<() => void> = []
let changelogInitialized = false

export const isFeaturebaseEnabled = () => FEATUREBASE_ENABLED

export const whenFeaturebaseBooted = (): Promise<void> => {
  if (isBooted) {
    return Promise.resolve()
  }

  return new Promise(resolve => {
    bootWaiters.push(resolve)
  })
}

const markFeaturebaseBooted = () => {
  if (isBooted) {
    return
  }

  isBooted = true
  const waiters = bootWaiters
  bootWaiters = []
  waiters.forEach(resolve => resolve())
}

const ensureFeaturebaseQueue = () => {
  if (typeof window.Featurebase === 'function') {
    return
  }

  const queued = (...args: unknown[]) => {
    const featurebaseQueue = queued as unknown as { q?: unknown[][] }
    featurebaseQueue.q = (featurebaseQueue.q || []).concat([args])
  }
  window.Featurebase = queued
}

const ensureFeaturebaseScript = () => {
  ensureFeaturebaseQueue()
  if (document.getElementById(FEATUREBASE_SCRIPT_ID)) {
    return
  }

  const script = document.createElement('script')
  script.id = FEATUREBASE_SCRIPT_ID
  script.src = FEATUREBASE_SCRIPT_SRC
  script.async = true

  const firstScript = document.getElementsByTagName('script')[0]
  if (firstScript?.parentNode) {
    firstScript.parentNode.insertBefore(script, firstScript)
  } else {
    document.head.appendChild(script)
  }
}

const callFeaturebase = (command: FeaturebaseCommand, ...args: unknown[]) => {
  if (!FEATUREBASE_ENABLED) {
    return
  }

  ensureFeaturebaseScript()
  window.Featurebase?.(command, ...args)
}

export const bootFeaturebase = (config: FeaturebaseBootConfig) => {
  if (!FEATUREBASE_ENABLED) {
    return
  }

  callFeaturebase('boot', {
    ...config,
    hideDefaultLauncher: true
  })
  markFeaturebaseBooted()
}

export const identifyFeaturebase = (config: FeaturebaseIdentifyConfig) => {
  callFeaturebase('identify', config)
}

export const showFeaturebase = (page?: 'home' | 'messages' | 'changelog' | 'help') => {
  if (page) {
    callFeaturebase('show', page)
    return
  }
  callFeaturebase('show')
}

export const hideFeaturebase = () => {
  callFeaturebase('hide')
}

export const showFeaturebaseNewMessage = (initialText?: string) => {
  if (initialText) {
    callFeaturebase('showNewMessage', initialText)
    return
  }
  callFeaturebase('showNewMessage')
}

export const onFeaturebaseHide = (callback: FeaturebaseCommandCallback) => {
  callFeaturebase('onHide', callback)
}

export const onFeaturebaseShow = (callback: FeaturebaseCommandCallback) => {
  callFeaturebase('onShow', callback)
}

export const setFeaturebaseLanguage = (language: string) => {
  callFeaturebase('setLanguage', language)
}

export const initFeaturebaseChangelogWidget = (organization: string) => {
  if (!FEATUREBASE_ENABLED || changelogInitialized) {
    return
  }

  const config: FeaturebaseChangelogWidgetConfig = {
    organization,
    theme: 'light',
    changelogCard: {
      enabled: true
    }
  }

  callFeaturebase('init_changelog_widget', config)
  changelogInitialized = true
}
